import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { useRef } from 'react';

import { storeDashboardAnalysis } from '../service/widget.service';
import { setChartClickResponse } from '../../../../redux/slices/page-header.slice';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const LineChartComponent = ({
  cubeResponse,
  annotation,
  dashboardDetails,
  dashboardLogDetails,
  valueAnnotation,
  formikData,
}) => {
  const labels = [];
  const values = [];
  const dispatch = useDispatch();
  const primaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--primary-color')
    .trim();

  const lightenColor = (hex, percent) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);

    const r = Math.min(255, (num >> 16) + amt);
    const g = Math.min(255, ((num >> 8) & 0x00ff) + amt);
    const b = Math.min(255, (num & 0x0000ff) + amt);

    return `rgb(${r}, ${g}, ${b})`;
  };

  const formatWithAnnotation = value => {
    let formatter = null;

    if (valueAnnotation) {
      Object.keys(valueAnnotation).forEach(key => {
        if (valueAnnotation?.[key]?.meta?.format) {
          formatter = eval(valueAnnotation[key].meta.format);
        }
      });
    }

    return formatter ? formatter(value) : value;
  };

  const lineValueLabelsPlugin = {
    id: 'lineValueLabels',
    afterDatasetsDraw(chart, args, pluginOptions) {
      const { ctx, chartArea } = chart;
      const datasetMeta = chart.getDatasetMeta(0);

      ctx.save();
      ctx.font = pluginOptions?.font || '12px sans-serif';
      ctx.fillStyle = pluginOptions?.color || '#334155';
      ctx.textBaseline = 'bottom';

      datasetMeta.data.forEach((point, index) => {
        const value = chart.data.datasets[0].data[index];
        if (value == null) return;
        const formattedValue = formatWithAnnotation(value);

        const { x, y } = point.getProps(['x', 'y'], true);

        const safeY = Math.max(
          chartArea.top + 16,
          Math.min(y - 14, chartArea.bottom - 16)
        );
        ctx.textAlign = 'left';
        ctx.fillText(formattedValue, x + 6, safeY);
      });

      ctx.restore();
    },
  };

  // Parse cubeResponse
  if (cubeResponse && cubeResponse.length) {
    const copiedResponse = _.cloneDeep(cubeResponse);
    for (const item of copiedResponse) {
      for (const k of Object.keys(item)) {
        if (
          k?.includes('avg') ||
          k?.includes('sum') ||
          k?.includes('count') ||
          k?.includes('min') ||
          k?.includes('max')
        ) {
          values.push(
            typeof item[k] === 'number'
              ? Number.isInteger(item[k])
                ? item[k]
                : +item[k].toFixed(3)
              : typeof item[k] === 'string' && !Number.isNaN(Number(item[k]))
                ? Number.isInteger(Number(item[k]))
                  ? Number(item[k])
                  : +Number(item[k]).toFixed(3)
                : item[k]
          );
          delete item[k];

          labels.push(
            Object.keys(item)
              ?.filter(f => f !== undefined)
              ?.map(record => {
                let formatter = null;
                Object.keys(item)?.forEach(i => {
                  if (annotation?.[i]?.meta?.format) {
                    formatter = eval(annotation[i].meta.format);
                  }
                });
                return formatter ? formatter(item[record]) : item[record];
              })
              ?.join(' | ')
          );
        }
      }
    }
  }

  // Chart data
  const data = {
    labels,
    datasets: [
      {
        label: '',
        data: values,
        borderWidth: 2,

        borderColor: formikData?.custom_properties?.chart_color || primaryColor,

        backgroundColor: context => {
          const ctx = context.chart.ctx;

          const gradient = ctx.createLinearGradient(0, 0, 0, ctx.canvas.height);

          gradient.addColorStop(
            0,
            lightenColor(
              formikData?.custom_properties?.chart_color || primaryColor,
              30
            )
          );
          gradient.addColorStop(
            1,
            formikData?.custom_properties?.chart_color || primaryColor
          );

          return gradient;
        },

        pointBackgroundColor:
          formikData?.custom_properties?.chart_color || primaryColor,

        pointBorderColor:
          formikData?.custom_properties?.chart_color || primaryColor,

        pointRadius: 3,
        pointHoverRadius: 4,
        fill: true,
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    responsive: true,
    animation: {
      duration: 1500,
      easing: 'easeOutQuart',
    },
    tension: 0.4,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
      colors: { enabled: false, forceOverride: true },
      lineValueLabels: {
        color: '#334155',
        font: '11px sans-serif',
        clip: false, // <-- allow labels to overflow chart area
      },
    },
    scales: {
      x: {
        ticks: {
          autoSkip: true,
          padding: 10,
          font: { size: 10 },
        },
      },
      y: {
        beginAtZero: true,
        ticks: { font: { size: 11 } },
      },
    },
    layout: {
      padding: {
        top: 30,
        bottom: 100,
      },
    },
    onClick: (event, elements, chart) => {
      if (elements.length) {
        const { index } = elements[0];

        const clickedRawData = cubeResponse?.[index];

        dispatch(setChartClickResponse(clickedRawData));
      }
      setTimeout(() => {
        const parentCard = chart.canvas.closest('.chartClick');
        parentCard.dataset.fromChart = 'true';
        if (parentCard) {
          parentCard.click();
        }
      }, 0);
    },

    interaction: {
      mode: 'nearest',
      intersect: true,
    },
  };

  if (dashboardDetails && dashboardLogDetails) {
    storeDashboardAnalysis({
      ...dashboardLogDetails,
      ...dashboardDetails,
      executionTime: new Date().getTime() - dashboardDetails.start_time,
      end_time: new Date().getTime(),
    });
  }
  const chartRef = useRef(null);

  return (
    <div
      style={{ height: '250px', width: '100%' }}
      onClick={e => {
        const chart = chartRef.current;
        if (!chart) {
          dispatch(setChartClickResponse(null));
          return;
        }

        const elements = chart.getElementsAtEventForMode(
          e.nativeEvent,
          'nearest',
          { intersect: true },
          true
        );

        if (elements.length) {
          e.stopPropagation();
          return;
        }

        dispatch(setChartClickResponse(null));
      }}
    >
      <Line
        data={data}
        options={options}
        plugins={[lineValueLabelsPlugin]}
        ref={chartRef}
      />
    </div>
  );
};

export default LineChartComponent;
